import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../config/api';

export interface PublishRunBody {
  platforms: string[];
  /**
   * Which connection to publish through. ISO 3166-1 alpha-2 (e.g. 'SE') picks
   * the country-scoped account; `null` picks the global brand account. Always
   * sent — omitting it would trigger the backend's legacy most-recent fallback.
   */
  country?: string | null;
  /**
   * The render format to publish (e.g. 'ig_square' | 'ig_portrait'). Sent for
   * runs with more than one publishable format so exactly one aspect-ratio
   * carousel is posted; omitting it on a multi-format run is a backend 400.
   */
  format?: string;
  /**
   * An admin-edited caption that replaces the run's generated caption for this
   * publish. Omitted when the admin has not edited the caption.
   */
  captionOverride?: string;
}

export interface PublishRunResult {
  posts: Array<{
    platform: string;
    postId: string | null;
    postUrl: string | null;
    status: 'published' | 'failed';
    error: string | null;
  }>;
}

export function usePublishRun(runId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishRunBody) =>
      api<PublishRunResult>(`/carousel/runs/${String(runId)}/publish`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['run', runId] });
      void qc.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}
